/**
 * Helpers for Depense category / subcategory UI (grouped selects, derived category id).
 */

function normalizeId(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || "");
  return String(value);
}

function resolveCategoryName(category) {
  if (!category) return "";
  if (typeof category === "string") return category;
  if (typeof category.name === "string") return category.name;
  if (category._id) return String(category._id);
  return String(category);
}

/**
 * From populated DepenseCategory.depenseType (object with expenseKind).
 * @param {{ depenseType?: object|string }|null|undefined} category
 * @returns {string|null}
 */
export function resolveExpenseKindFromCategory(category) {
  if (!category || !category.depenseType) return null;
  const dt = category.depenseType;
  if (typeof dt === "object" && dt != null && dt.expenseKind) return String(dt.expenseKind);
  return null;
}

/**
 * @param {Array<{ _id: string, name?: string, depenseType?: object|string }>} categories
 * @param {Array<{ _id: string, name?: string, category: string }>} subcategories
 * @returns {Array<{ categoryId: string, categoryName: string, expenseKind: string|null, subs: typeof subcategories }>}
 */
export function buildSubcategoryGroups(categories, subcategories) {
  const cats = categories || [];
  const subs = subcategories || [];
  const catMap = new Map(cats.map((c) => [normalizeId(c && c._id), resolveCategoryName(c)]));
  const catById = new Map(cats.map((c) => [normalizeId(c && c._id), c]));
  const byCat = new Map();
  for (const s of subs) {
    const cid = normalizeId(s && s.category);
    if (!cid) continue;
    if (!byCat.has(cid)) byCat.set(cid, []);
    byCat.get(cid).push(s);
  }
  const groupKeys = [...byCat.keys()].sort((a, b) => {
    const na = catMap.get(a) != null ? catMap.get(a) : String(a);
    const nb = catMap.get(b) != null ? catMap.get(b) : String(b);
    return String(na).localeCompare(String(nb), undefined, { sensitivity: "base" });
  });
  return groupKeys.map((categoryId) => {
    const cat = catById.get(categoryId);
    return {
      categoryId,
      categoryName: catMap.get(categoryId) != null ? catMap.get(categoryId) : String(categoryId),
      expenseKind: resolveExpenseKindFromCategory(cat),
      subs: (byCat.get(categoryId) || []).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      ),
    };
  });
}

/**
 * Parent DepenseCategory id for a given DepenseSubCategory id.
 * @param {Array<{ _id: string, category: string }>} subcategories
 * @param {string} [subcategoryId]
 * @returns {string|undefined}
 */
export function categoryIdForSubcategory(subcategories, subcategoryId) {
  if (!subcategoryId) return undefined;
  const sid = normalizeId(subcategoryId);
  const s = (subcategories || []).find((x) => normalizeId(x && x._id) === sid);
  const cid = s ? normalizeId(s.category) : "";
  return cid || undefined;
}

/**
 * Ant Design Vue <a-select show-search> filter: matches subcategory name and parent category name.
 */
export function createSubcategoryFilter(categories, subcategories) {
  const cats = categories || [];
  const subs = subcategories || [];
  return function filterSubcategoryOption(input, option) {
    let sid;
    const props = option.componentOptions && option.componentOptions.propsData;
    if (props && props.value !== undefined && props.value !== null) {
      sid = props.value;
    } else if (option.data && option.data.attrs) {
      sid = option.data.attrs.value;
    } else {
      return true;
    }
    const sidNormalized = normalizeId(sid);
    const sub = subs.find((s) => normalizeId(s && s._id) === sidNormalized);
    if (!sub) return false;
    const subCategoryId = normalizeId(sub.category);
    const cat = cats.find((c) => normalizeId(c && c._id) === subCategoryId);
    const q = (input || "").toLowerCase();
    const ek = resolveExpenseKindFromCategory(cat) || "";
    const hay = `${cat ? resolveCategoryName(cat) : ""} ${sub.name || ""} ${ek}`.toLowerCase();
    return hay.includes(q);
  };
}
